import { useEffect } from 'react';
import { useTitle, useToggle } from 'react-use';
import { Common } from '@thecvlb/design-system/lib/src';
import { useFlag } from '@unleash/proxy-client-react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';

import { CreditCardAttributesProps } from 'services/myAccount/myAccount.types';

import { selectOrchestrate, selectSignUp } from 'store';
import { setPayment } from 'store/orchestrate-signup/orchestrateSlice';
import { ValidCouponCodes } from 'store/signup/signup.types';

import { FirstAppointmentInfo } from 'pages/SignUp/components';
import OptaviaBenefitsCard from 'pages/SignUp/WM/Checkout/OptaviaBenefitsCard';

import { BasicSignupStepProps } from 'containers/SignUp/Content/content.types';
import { checkIfUserIsMedicaid, getScalesPriceByPPLength } from 'containers/SignUp/signup.settings';
import AdvantageItem from 'features/AdvantageItem';
import NewWeightGraph from 'features/NewWeightGraph';
import NewAppointmentTime from 'modals/NewAppointmentTimeModal';
import { notifySuccess } from 'shared/Toast/Toast';
import { buildCreditCardAttributesFromForm } from 'widgets/PaymentFormNew/paymentFormNew.settings';
import { PaymentFormFields } from 'widgets/PaymentFormNew/paymentFormNew.types';
import PaymentForm from 'widgets/signUp/PaymentForm';
import { PaypalFormInfo } from 'widgets/signUp/PaymentForm/paymentForm.types';
import { getApptAdvantages } from 'widgets/signUp/stepWrappers/PlanSelectStepWrapper/planSelect.settings';

import { DUMMY_PRICE_OF_AT_HOME_LABS } from 'constants/defaults';
import { WM_FLOWS } from 'constants/onboarding';
import {
  BLUE_LINE_ONBOARDING_PRICE_POINTS,
  SEMAGLUTIDE_PRICE_POINT,
  TIRZEPATIDE_PRICE_POINT
} from 'constants/pricepoints';
import { useAppDispatch, useAppSelector } from 'hooks';
import useSubmitOrchestrateForm from 'hooks/useSubmitOrchestrateForm';
import useWidth from 'hooks/useWidth';
import { appendScript } from 'utils/appendScript';
import {
  BLUE_LINE_ADVANTAGES,
  OPTAVIA_DUMMY_ADVANTAGES,
  WM_DUMMY_ADVANTAGES,
  WM_INSURANCE_DUMMY_ADVANTAGES
} from 'utils/constants';
import { FeatureFlag, FlowTypes, PlanCodes } from 'utils/enums';
import { handleEverFlowEvent } from 'utils/everflow';
import { createGtmEvent } from 'utils/gtm';
import {
  filterBalladHealthAdvantage,
  getOrderTotalPrice,
  getScalesAddonData,
  getSpecificCouponCode,
  parseTime
} from 'utils/helpers';
import { handlePostHogEvent, handlePostHogPurchase } from 'utils/posthog';

import Heading from '../../components/Heading';
import PaymentSummaryCard from '../parts/PaymentSummaryCard';
import WMScalesCheckbox from '../parts/WMScalesCheckbox';
import { getNewTargetWeight } from '../wm.settings';

import { AppointmentTime } from 'models/appointment.types';

import { getDataForPayment } from './checkout.settings';

const Checkout: React.FC<BasicSignupStepProps> = ({ selectedFlow, moveToStep }) => {
  const showScales =
    useFlag(FeatureFlag.OnboardingWMScales) &&
    [
      FlowTypes.WeightManagementFlow,
      FlowTypes.BlueLineFlow,
      FlowTypes.WeightManagementBalladHealth
    ].includes(selectedFlow);

  const dispatch = useAppDispatch();
  const { isMobile } = useWidth();
  const {
    payment: { product_price_point_handle, coupon_codes, includesScales },
    mif_qa: mifQA,
    appointment: { appointmentTime },
    insurances,
    user: { isAsyncSignup }
  } = useAppSelector(selectOrchestrate);
  useTitle('Checkout');
  const { send, isLoading: isSendingOrchestrate } = useSubmitOrchestrateForm();
  const { user, membershipPlans } = useAppSelector(selectSignUp);

  const primaryInsuranceData = insurances?.[0];

  const currentWeight = Number(mifQA.find((q) => q.question === 'weight')?.answer) ?? null;
  const willUseInsurance =
    mifQA.find((q) => q.question === 'will_use_insurance_for_medications')?.answer ?? false;
  const newTargetWeight = getNewTargetWeight(currentWeight);
  const doesIncludesLabsAtHome = mifQA.find((q) => q.question === 'labs')?.answer === 'ash';
  const insuranceAnswer = mifQA.find((a) => a.question === 'insurance_types')?.answer ?? '';

  const WMPLan = membershipPlans.find((p) => p.planCode === PlanCodes.WeightManagementMembership);

  const WMPricePoints = [...(WMPLan?.pricePoints ?? [])];
  const selectedPP = WMPricePoints?.find(
    (pp) => pp.planPricePointId === product_price_point_handle
  );

  const data = selectedPP
    ? getDataForPayment(selectedPP, coupon_codes[0] as ValidCouponCodes, selectedFlow)
    : null;

  const [isOpen, togglePopup] = useToggle(false);

  const onToggleScales = () => dispatch(setPayment({ includesScales: !includesScales }));

  const isCompoundMedication = [SEMAGLUTIDE_PRICE_POINT, TIRZEPATIDE_PRICE_POINT].includes(
    product_price_point_handle
  );

  const priceWithDiscount = getScalesPriceByPPLength(selectedPP);
  const apptAdvantages = getApptAdvantages(WMPLan, selectedFlow, isCompoundMedication);

  const onFormSubmit = (
    formData: PaymentFormFields | PaypalFormInfo,
    paymentMethod: CreditCardAttributesProps['paymentMethod'] = 'credit_card'
  ) => {
    const regularBody = buildCreditCardAttributesFromForm(formData, paymentMethod);
    const parsedBody = {
      cvv: regularBody.cvv ?? '',
      expiration_month: regularBody.expirationMonth ?? '',
      expiration_year: regularBody.expirationYear ?? '',
      full_number: regularBody.fullNumber ?? ''
    };

    const addonData = getScalesAddonData(selectedPP, includesScales);
    send(
      'payment',
      { addOns: !!addonData && showScales ? [addonData] : [], credit_card_attributes: parsedBody },
      () => {
        if (WMPLan && selectedPP) {
          const order = {
            couponCode: getSpecificCouponCode(
              (coupon_codes[0] as ValidCouponCodes) || '',
              WMPLan.planCode
            ),
            customer_id: user.customerId,
            orderId: user.subscriptionId,
            orderTotal: getOrderTotalPrice(WMPLan, selectedPP),
            orderTs: new Date().toISOString(),
            productId: WMPLan._id,
            productName: WMPLan.planName
          };
          createGtmEvent('purchase', user, order);
          handlePostHogPurchase(selectedFlow, order);

          const getAdditionalEventName = () => {
            if (
              willUseInsurance &&
              ['ACTIVE', 'ACTIVE_AT_RISK'].includes(
                String(primaryInsuranceData?.eligibilityResponse?.status)
              )
            ) {
              return 'wm-purchase-ins';
            }
            return 'wm-purchase-cash';
          };
          handlePostHogEvent(getAdditionalEventName());
          handlePostHogEvent('wmp-purchase', { flow: selectedFlow, ...order });
          handleEverFlowEvent(getOrderTotalPrice(WMPLan, selectedPP), `${user.subscriptionId}`);
          appendScript(import.meta.env.BASE_URL + './js/voluumScript.js');
          createGtmEvent('submit_payment', user);
        }
        const isVerifiedFailed = sessionStorage.getItem('identity-verified') === 'false';
        if (isVerifiedFailed) {
          moveToStep('next', { searchParams: '?verification-failed=1' });
        } else {
          moveToStep('next');
        }
      },
      'USER',
      (v: unknown) => {
        const casted = v as { data: { message: string } };
        if (casted?.data?.message && casted.data.message === 'The slot is no longer available') {
          togglePopup(true);
        }
      }
    );
  };

  const reSubmitPayment = async (time: AppointmentTime, newProviderID?: string | null) => {
    if (time) {
      send(
        'appointment',
        {
          ...(newProviderID && { doctorId: newProviderID }),
          appointmentTime: {
            endTime: time.endTime ?? '',
            startTime: time.startTime ?? ''
          },
          planId: WMPLan?._id,
          timezone: dayjs.tz?.guess()
        },
        () => {
          togglePopup(false);
          notifySuccess('Appointment time rescheduled successfully, please confirm your payment');
        },
        undefined,
        () => {
          throw new Error('Something went wrong. Please try again later.');
        }
      );
    }
  };
  const isWMFlow = WM_FLOWS.includes(selectedFlow);

  const isOptaviaFlow = selectedFlow === FlowTypes.WeightManagementFlowOptavia;

  const getAdvantages = () => {
    const basicList =
      selectedFlow === FlowTypes.BlueLineFlow
        ? BLUE_LINE_ADVANTAGES
        : isOptaviaFlow
          ? OPTAVIA_DUMMY_ADVANTAGES
          : willUseInsurance
            ? WM_INSURANCE_DUMMY_ADVANTAGES
            : WM_DUMMY_ADVANTAGES;

    return basicList.filter((a) => {
      const isMedicaidUser = checkIfUserIsMedicaid(insuranceAnswer);
      if (selectedFlow === FlowTypes.WeightManagementBalladHealth) {
        return filterBalladHealthAdvantage(a);
      }
      if (!isMedicaidUser) {
        return true;
      }
      return !a.toLowerCase().includes('insurance');
    });
  };
  const advantages = getAdvantages();

  const getAdditionalPurchaseInfo = () => {
    if (includesScales) {
      return {
        color: 'text-secondary-600',
        header: 'Withings Body Pro 2 Scale',
        price: priceWithDiscount ?? '',
        subheader: 'Shipping included with Rx'
      };
    }
    if (doesIncludesLabsAtHome) {
      return {
        color: 'text-primary-700',
        header: 'At-home lab kit',
        price: DUMMY_PRICE_OF_AT_HOME_LABS
      };
    }
    return undefined;
  };

  const isBlueLinePP = BLUE_LINE_ONBOARDING_PRICE_POINTS.includes(product_price_point_handle);

  useEffect(() => {
    if (user.status === 'active') {
      togglePopup(true);
    }
  }, [membershipPlans]);

  return (
    <>
      <NewAppointmentTime
        bufferPeriodDays={1}
        flow={selectedFlow}
        isOpen={isOpen}
        loading={isSendingOrchestrate}
        reason="expired-slot"
        persist
        onClose={togglePopup}
        onConfirm={reSubmitPayment}
      />
      <div className="flex flex-col gap-6">
        <Heading category="Sign up" title="Confirm and pay" />
        <div className="flex gap-6 max-md:flex-col md:gap-11">
          <div className="flex flex-col gap-8 md:w-1/2">
            {!!data && isWMFlow && (
              <div className="flex w-full flex-col gap-6 max-md:self-stretch">
                <PaymentSummaryCard
                  additionalPurchaseInfo={getAdditionalPurchaseInfo()}
                  data={data}
                  insurance={willUseInsurance ? primaryInsuranceData : undefined}
                  isBlueLinePP={isBlueLinePP}
                  isCompoundMedication={isCompoundMedication}
                />
                {showScales && (
                  <WMScalesCheckbox
                    isSelected={!!includesScales}
                    priceWithDiscount={priceWithDiscount}
                    onToggle={onToggleScales}
                  />
                )}
              </div>
            )}
            <div>
              <PaymentForm
                flow={selectedFlow}
                isOneTimeDiscount={!!willUseInsurance || selectedPP?.paymentInterval.qty !== 3}
                isShowBirthInput={false}
                loading={isSendingOrchestrate}
                placement={isCompoundMedication ? 'compound' : 'wm-flow-checkout'}
                showHeading={isMobile}
                onFormSubmit={(formValues) => onFormSubmit(formValues, 'credit_card')}
                // onPaypalSubmit={(formValues) => onFormSubmit(formValues, 'paypal_account')}
              />
            </div>
            {!isAsyncSignup && (
              <div className="md:hidden">
                <FirstAppointmentInfo
                  advantages={apptAdvantages}
                  startTime={appointmentTime?.startTime || ''}
                />
              </div>
            )}
            {!isOptaviaFlow &&
              (selectedFlow === FlowTypes.TripleTherapy ? (
                <div className="rounded-2xl shadow md:hidden">
                  <div className="flex gap-4 rounded-t-2xl bg-primary-100 p-6 text-mXl text-primary-700">
                    <div className="flex size-12 flex-none rounded-full bg-primary-200">
                      <Common.Icon className="m-auto size-8 text-primary" name="doctor" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <h2 className="text-mLg font-bold md:text-xl">Appointment</h2>
                      <span className="text-mSm md:text-base">
                        Your provider • {parseTime(appointmentTime?.startTime ?? '')}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 p-4 text-mSm">
                    {[
                      <span>
                        <span className="font-bold">No needles, no complicated routines</span> —
                        just convenient oral medication that fits seamlessly into your daily life
                        for effective weight loss support.
                      </span>,
                      <span>
                        <span className="mr-1 font-bold">Affordable</span>
                        medication that is significantly less expensive than GLP-1 medications,
                        making effective weight loss more accessible.
                      </span>,
                      <span>
                        <span className="font-bold">Ongoing clinical support</span> from licensed
                        medical professionals through messaging and video appointments.
                      </span>
                    ].map((el) => (
                      <div className="flex items-center gap-2" key={nanoid()}>
                        <Common.Icon className="flex-none text-secondary" name="check" />
                        {el}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-5 rounded-2xl bg-white p-4 md:hidden">
                  {!!newTargetWeight && (
                    <div className="flex flex-col gap-1">
                      <Common.ColorTag color="secondary" size="sm" text="Goal" />
                      <h2 className="mb-6 font-bold text-primary-700">
                        Lose {currentWeight - Number(newTargetWeight)} pounds in 6-12 months
                      </h2>
                      <NewWeightGraph weightPoints={[currentWeight, newTargetWeight]} />
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <h2 className="text-mLg font-semibold text-primary-700 md:text-lg">
                      LifeMD GLP-1 Weight Management {selectedPP?.paymentInterval.qty}-Month
                      Program:
                    </h2>
                    {advantages.map((el) => (
                      <AdvantageItem
                        advantage={el}
                        className="text-mSm md:text-base"
                        key={el}
                        showCustomIcon
                      />
                    ))}
                  </div>
                </div>
              ))}
          </div>
          <div className="flex flex-col gap-6 md:w-1/2 md:gap-8">
            {!!data && !isWMFlow && (
              <div className="flex w-full flex-col gap-6 max-md:self-stretch">
                <PaymentSummaryCard
                  additionalPurchaseInfo={getAdditionalPurchaseInfo()}
                  data={data}
                  insurance={willUseInsurance ? primaryInsuranceData : undefined}
                  isBlueLinePP={isBlueLinePP}
                  isCompoundMedication={isCompoundMedication}
                />
                {showScales && (
                  <WMScalesCheckbox
                    isSelected={!!includesScales}
                    priceWithDiscount={priceWithDiscount}
                    onToggle={onToggleScales}
                  />
                )}
              </div>
            )}
            {selectedFlow === FlowTypes.TripleTherapy ? (
              <div className="rounded-2xl shadow max-md:hidden">
                <div className="flex gap-4 rounded-t-2xl bg-primary-100 p-6 text-mXl text-primary-700">
                  <div className="flex size-12 flex-none rounded-full bg-primary-200">
                    <Common.Icon className="m-auto size-8 text-primary" name="doctor" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <h2 className="text-mLg font-bold md:text-xl">Appointment</h2>
                    <span className="text-mSm md:text-base">
                      Your provider • {parseTime(appointmentTime?.startTime ?? '')}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 p-4 text-mSm">
                  {[
                    <span>
                      <span className="font-bold">No needles, no complicated routines</span> — just
                      convenient oral medication that fits seamlessly into your daily life for
                      effective weight loss support.
                    </span>,
                    <span>
                      <span className="mr-1 font-bold">Affordable</span>
                      medication that is significantly less expensive than GLP-1 medications, making
                      effective weight loss more accessible.
                    </span>,
                    <span>
                      <span className="font-bold">Ongoing clinical support</span> from licensed
                      medical professionals through messaging and video appointments.
                    </span>
                  ].map((el) => (
                    <div className="flex items-center gap-2" key={nanoid()}>
                      <Common.Icon className="flex-none text-secondary" name="check" />
                      {el}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                {!isAsyncSignup && (
                  <div className="mb-4 max-md:hidden">
                    <FirstAppointmentInfo
                      advantages={apptAdvantages}
                      startTime={appointmentTime?.startTime || ''}
                    />
                  </div>
                )}
                <div
                  className={classNames('flex flex-col gap-2', {
                    'rounded-2xl bg-white p-4 shadow max-md:hidden': !isOptaviaFlow
                  })}
                >
                  {!isOptaviaFlow && !!newTargetWeight && (
                    <div className="flex flex-col gap-1">
                      <Common.ColorTag color="secondary" size="sm" text="Goal" />
                      <h2 className="mb-6 font-bold text-primary-700">
                        Lose {currentWeight - Number(newTargetWeight)} pounds in 6-12 months
                      </h2>
                      <NewWeightGraph weightPoints={[currentWeight, newTargetWeight]} />
                    </div>
                  )}
                  <h2 className="font-bold text-primary-700">
                    {selectedFlow === FlowTypes.WeightManagementFlowOptavia
                      ? 'LifeMD Weight Management Support includes:'
                      : `LifeMD GLP-1 Weight Management ${selectedPP?.paymentInterval.qty}-Month Program:`}
                  </h2>
                  {advantages.map((el) => (
                    <AdvantageItem advantage={el} key={el} showCustomIcon />
                  ))}
                </div>
              </div>
            )}
            {selectedFlow === FlowTypes.WeightManagementFlowOptavia && (
              <div className="flex flex-col gap-4">
                <OptaviaBenefitsCard />
                <p className="text-mSm text-gray md:text-base">
                  Providers will prescribe medication based on your current weight, your past
                  medical history and your insurance coverage. Medication is not included with this
                  plan.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkout;
